// covered-by: frontends/carrie/src/ask-v2/review/__tests__/useAskPunttReview.test.tsx
import {
  type AskPunttReviewCommentWriteBody,
  type GetAskPunttReviewResponse,
} from "@mg/schemas/src/christo/askPunttReview";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  createAskPunttReviewComment,
  getAskPunttReview,
  updateAskPunttReviewComment,
} from "../services/reviews";

export const askV2ReviewKeys = {
  all: ["ask-v2-review"] as const,
  detail: (reviewId: string) =>
    [...askV2ReviewKeys.all, "detail", reviewId] as const,
};

export function useAskPunttReview(
  reviewId: string | null | undefined,
  options?: { enabled?: boolean },
) {
  const enabled = (options?.enabled ?? true) && !!reviewId;
  return useQuery({
    queryKey: askV2ReviewKeys.detail(reviewId ?? ""),
    queryFn: () => getAskPunttReview(reviewId!),
    enabled,
  });
}

export function useCreateAskPunttReviewComment(
  reviewId: string | null | undefined,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: AskPunttReviewCommentWriteBody) => {
      if (!reviewId) {
        throw new Error("A reviewId is required to add a review comment");
      }
      return createAskPunttReviewComment(reviewId, body);
    },
    onSuccess: ({ comment }) => {
      if (!reviewId) return;
      const key = askV2ReviewKeys.detail(reviewId);
      queryClient.setQueryData<GetAskPunttReviewResponse>(key, (prev) => {
        if (!prev) return prev;
        if (prev.review.comments.some((c) => c._id === comment._id)) {
          return prev;
        }
        return {
          ...prev,
          review: {
            ...prev.review,
            comments: [...prev.review.comments, comment],
            commentsSummary: {
              ...prev.review.commentsSummary,
              human: prev.review.commentsSummary.human + 1,
            },
          },
        };
      });
      void queryClient.invalidateQueries({ queryKey: key });
    },
  });
}

export function useUpdateAskPunttReviewComment(
  reviewId: string | null | undefined,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      commentId,
      body,
    }: {
      commentId: string;
      body: Partial<AskPunttReviewCommentWriteBody>;
    }) => {
      if (!reviewId) {
        throw new Error("A reviewId is required to update a review comment");
      }
      return updateAskPunttReviewComment(reviewId, commentId, body);
    },
    onSuccess: ({ comment }) => {
      if (!reviewId) return;
      const key = askV2ReviewKeys.detail(reviewId);
      queryClient.setQueryData<GetAskPunttReviewResponse>(key, (prev) => {
        if (!prev) return prev;
        const hasComment = prev.review.comments.some(
          (item) => item._id === comment._id,
        );
        if (!hasComment) return prev;
        return {
          ...prev,
          review: {
            ...prev.review,
            comments: prev.review.comments.map((item) =>
              item._id === comment._id ? comment : item,
            ),
          },
        };
      });
    },
  });
}
